import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Pagination, Tag } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { CURRENCY, Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Alert = {
  id: string; rmlId: number; userId: number | null; nickname: string; game: string; amount: string; at: string;
  ackAt: string | null; ackByName: string | null;
};
type Page = { items: Alert[]; total: number; page: number; pageSize: number; games: string[] };

const KEYS = ['state', 'game', 'userId', 'nick', 'amountMin', 'from', 'to'] as const;

// Big-win alert history (P1-D02): every alert the stats worker raised, open or acknowledged.
export function AlertsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const f = useUrlFilters(KEYS, { defaults: { state: 'all' } });
  const q = useQuery({
    queryKey: ['alerts-history', f.values, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/alerts/history', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/alerts/${id}/ack`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alerts-history'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
  });
  const fields: FilterField[] = [
    { key: 'state', label: 'Trạng thái', kind: 'select', options: [['all', 'Tất cả'], ['open', 'Chưa xem'], ['acked', 'Đã xem']] },
    { key: 'game', label: 'Game', kind: 'select', options: [['', 'Tất cả'], ...(q.data?.games ?? []).map((g) => [g, g] as const)] },
    { key: 'userId', label: 'Mã người chơi', kind: 'number' },
    { key: 'nick', label: 'Nick', kind: 'text' },
    { key: 'amountMin', label: `Thắng từ (${CURRENCY.symbol})`, kind: 'number' },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  const columns: ColumnDef<Alert, any>[] = [
    { header: 'Lúc', accessorKey: 'at', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Người chơi', accessorKey: 'nickname', cell: (c) => c.row.original.userId && can('users.view')
      ? <Link className="cell-link" to={`/users/${c.row.original.userId}`}>{c.getValue()}</Link> : c.getValue() },
    { header: 'Game', accessorKey: 'game' },
    { header: `Thắng (${CURRENCY.symbol})`, accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
    { header: 'Dòng sổ', accessorKey: 'rmlId', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Trạng thái', id: 'st', enableSorting: false, cell: (c) => {
      const a = c.row.original;
      return a.ackAt
        ? <span><Tag type="gray" size="sm">Đã xem</Tag> <span className="muted"><When v={a.ackAt} /></span></span>
        : <Button kind="ghost" size="sm" disabled={ack.isPending && ack.variables === a.id} aria-label={`Đánh dấu đã xem thắng lớn của ${a.nickname}`}
          onClick={() => ack.mutate(a.id)}>Đánh dấu đã xem</Button>;
    } },
  ];
  return (
    <div className="page">
      <PageHeader title="Cảnh báo thắng lớn" lead="Mọi lần trả thưởng từ ngưỡng trong Cài đặt trở lên, cả đã xem và chưa xem." />
      <FilterBar id="al" fields={fields} filters={f} exportPath="/api/admin/alerts/history" />
      {ack.error ? <InlineError error={ack.error} /> : null}
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} rowKey={(r) => r.id}
        empty={{ title: f.active ? 'Không có cảnh báo khớp bộ lọc' : 'Chưa có cảnh báo thắng lớn', body: f.active ? 'Nới bộ lọc để xem thêm.' : <>Ngưỡng nằm trong <Link to="/settings">Cài đặt</Link>.</> }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} cảnh báo`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
    </div>
  );
}
